import React, { useEffect, useMemo, useState } from 'react';
import { Empty, Input, Spin, Tag } from 'antd';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
    GET_ORDER_QUOTA_HUB_OVERVIEW,
    OrderQuotaHubSummary,
    OrderQuotaStrategy,
    OrderQuotaStrategyType,
} from '../graphql/orderQuotaGraphql';
import { formatQuota } from '../utils/orderQuotaGrid';

interface OrderQuotaHubListProps {
    strategyType: OrderQuotaStrategyType;
    selectedHubId?: string;
    onSelect: (hubId: string) => void;
    /** 变化时重新拉取列表（如开关、HUB 产能、客户分级变化后） */
    refreshKey: number;
}

/** 选中项：主题色浅底（同左侧菜单选中项）加左侧紫色竖条（同网格展开行） */
const SELECTED_CLASS = 'bg-[#f9f0ff] shadow-[inset_3px_0_0_#722ed1]';

/** 列表展示的 HUB 名称；名称为空时用 HUB 编码 */
const hubLabel = (hub: OrderQuotaHubSummary): string => hub.hubName || hub.hubCode;

/** 统一大小写，空格、_、- 视为词的分隔，前面补一个空格便于按词首匹配 */
const normalize = (text: string): string => ` ${text.trim().toLowerCase()}`.replace(/[\s_-]+/g, ' ');

/**
 * 按展示的名称搜索：输入须出现在名称开头，或空格、_、- 之后（词首），如 la 命中 LAX HUB，不命中 Atlanta、TEST4 VLAX HUB；
 * 分拣码不展示，不参与匹配。
 */
const matchesKeyword = (hub: OrderQuotaHubSummary, keyword: string): boolean =>
    normalize(hubLabel(hub)).includes(normalize(keyword));

/**
 * 页面左侧 HUB 列表：搜索、展示开关状态与产能概况，点击切换右侧详情。
 */
const OrderQuotaHubList: React.FC<OrderQuotaHubListProps> = ({ strategyType, selectedHubId, onSelect, refreshKey }) => {
    const { t } = useTranslation();
    const [keyword, setKeyword] = useState('');
    const { data, loading, refetch } = useQuery<{ onboardingOrderQuotaHubOverview: OrderQuotaHubSummary[] }>(
        GET_ORDER_QUOTA_HUB_OVERVIEW,
        { variables: { strategyType }, fetchPolicy: 'network-only' }
    );

    useEffect(() => {
        if (refreshKey > 0) refetch();
    }, [refreshKey, refetch]);

    const hubs = useMemo(() => data?.onboardingOrderQuotaHubOverview ?? [], [data]);

    useEffect(() => {
        if (!selectedHubId && hubs.length > 0) onSelect(hubs[0].hubId);
    }, [hubs, selectedHubId, onSelect]);

    const filtered = useMemo(() => {
        if (!keyword.trim()) return hubs;
        return hubs.filter((hub) => matchesKeyword(hub, keyword));
    }, [hubs, keyword]);

    const summaryOf = (hub: OrderQuotaHubSummary) => {
        if (strategyType === OrderQuotaStrategy.CUSTOMER_PICKUP_HUB) {
            const quota = hub.hubDailyQuota > 0
                ? t('central.orderQuota.hubList.hubQuota', { quota: formatQuota(hub.hubDailyQuota) })
                : t('central.orderQuota.hubList.hubQuotaZero');
            return `${quota} · ${t('central.orderQuota.hubList.customers', { configured: hub.configuredRowCount, total: hub.rowCount })}`;
        }
        return hub.configuredRowCount > 0
            ? t('central.orderQuota.hubList.configured', { configured: hub.configuredRowCount, total: hub.rowCount })
            : t('central.orderQuota.hubList.allUnlimited');
    };

    return (
        <div className="w-72 shrink-0 border-r border-gray-100 flex flex-col">
            <div className="p-3">
                <Input.Search
                    allowClear
                    placeholder={t('central.orderQuota.hubList.search') as string}
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                />
            </div>
            <div className="flex-1 overflow-auto">
                {loading && hubs.length === 0 ? (
                    <div className="flex justify-center py-6"><Spin /></div>
                ) : filtered.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    filtered.map((hub) => {
                        const selected = hub.hubId === selectedHubId;
                        return (
                            <button
                                type="button"
                                key={hub.hubId}
                                aria-current={selected || undefined}
                                onClick={() => onSelect(hub.hubId)}
                                className={`w-full text-left px-4 py-3 border-b border-gray-50 ${
                                    selected ? SELECTED_CLASS : 'hover:bg-gray-50'
                                }`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className={`font-medium ${selected ? 'text-[#722ed1]' : ''}`}>{hubLabel(hub)}</span>
                                    {!hub.enabled && <Tag>{t('central.orderQuota.closed')}</Tag>}
                                </div>
                                <div className="text-xs text-gray-400 mt-1">{summaryOf(hub)}</div>
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default OrderQuotaHubList;
